import { useNavigate } from "react-router-dom";
import styles from "./OfflineApplicationUpload.module.css";
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { CustomButton, CustomFileInput } from "../../../../components";
import { useSelector } from "react-redux";
import CustomMRTTable from "../../../../components/CustomMRTTable/CustomMRTTable";
import AxiosInstance, {
  IMAGE_BASE_URL,
  TokenService,
} from "../../../../services/Api";
import { FaEye, FaTable } from "react-icons/fa";
import PopupModal from "../../../../components/PopupModal/PopupModal";
import moment from "moment";

function OfflineApplicationUpload() {
  const navigate = useNavigate();
  const user = TokenService.getUser();
  const { translations } = useSelector((state) => state.language);
  const schoolId = user?.client_data[0].school_id || "";
  const academic_year_id = user?.client_data[0].academic_year_id || "";
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState("");
  const [apiSearch, setApiSearch] = useState("");
  const [formData, setFormData] = useState({
    file: null,
    original_name: null,
    upload_path: null,
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const [modalState, setModalState] = useState({
    open: false,
    type: "",
    message: "",
    redirectPath: "",
  });

  const handleModalClose = () => {
    setModalState({ open: false, type: "", message: "", redirectPath: "" });
  };

  const handleSuccessOk = () => {
    handleModalClose();
    if (modalState.redirectPath) {
      navigate(modalState.redirectPath);
    }
  };

  // Table columns configuration for CustomMRTTable
  const columns = useMemo(
    () => [
      {
        accessorKey: "id",
        header: translations.labels?.serialNo || "S.No",
        size: 80,
        Cell: ({ row }) => row.index + 1,
      },
      {
        accessorKey: "original_name",
        header: translations.labels?.fileName || "File Name",
        size: 200,
        Cell: ({ row }) => {
          const fileName = row.original.file_name;
          const originalName = row.original.original_name;

          const handleView = async () => {
            if (!fileName) return;

            // legacy files on local disk (path starts with "/")
            if (fileName.startsWith("/")) {
              window.open(`${IMAGE_BASE_URL}${fileName}`, "_blank");
              return;
            }

            // Azure blob file — fetch through the authenticated API (blob logic)
            try {
              const response = await AxiosInstance.get(
                `/content/image?path=${encodeURIComponent(fileName)}`,
                { responseType: "blob" },
              );
              const objectUrl = URL.createObjectURL(
                new Blob([response.data], {
                  type: response.headers["content-type"] || "application/pdf",
                }),
              );
              window.open(objectUrl, "_blank");
            } catch (error) {
              console.error("Failed to open file:", error);
              setModalState({
                open: true,
                type: "error",
                message:
                  error.response?.data?.message ||
                  "Unable to open the file. Please try again.",
                redirectPath: "",
              });
            }
          };

          return (
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span>{originalName || "-"}</span>
              {fileName && (
                <button
                  className={styles.viewBtn}
                  onClick={handleView}
                  title={translations.labels?.viewStudent || "View Student"}
                >
                  <FaEye />
                </button>
              )}
            </div>
          );
        },
      },
      {
        accessorKey: "file_size",
        header: translations.labels?.sizeKb || "Size (KB)",
        size: 120,
        Cell: ({ row }) => row.original.file_size || "-",
      },
      {
        accessorKey: "created_at",
        header: translations.labels?.lastModified || "Last Modified",
        size: 200,
        Cell: ({ row }) =>
          row.original.created_at
            ? moment(row.original.created_at).format("DD/MM/YYYY hh:mm A")
            : "-",
      },
      {
        accessorKey: "status",
        header: translations.labels?.status || "Status",
        size: 150,
        Cell: ({ row }) => {
          const status = row.original.status;
          if (status === "success") {
            return <span style={{ color: "green" }}>Success</span>;
          }
          if (status === "processing") {
            return <span style={{ color: "orange" }}>Processing</span>;
          }
          return <span style={{ color: "red" }}>{status || "Failed"}</span>;
        },
      },
    ],
    [translations],
  );

  // File Onchange
  const handleFileChange = async (file) => {
    if (!file) return;

    const allowedTypes = [
      "application/pdf",
      "image/jpeg",
      "image/jpg",
      "image/png",
    ];

    if (!allowedTypes.includes(file.type)) {
      setModalState({
        open: true,
        type: "error",
        message: "Only PDF and Image files (JPG, JPEG, PNG) are allowed.",
        redirectPath: "",
      });
      return;
    }

    try {
      setIsUploading(true);
      const form = new FormData();
      form.append("content_image", file);
      form.append("type", "OfflineApplications");
      form.append("school_id", schoolId);

      const response = await AxiosInstance.post("/content/upload", form, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response?.data?.length) {
        const uploadData = response.data[0];

        setFormData({
          file: file,
          original_name: uploadData.original_name,
          upload_path: uploadData.upload_path,
        });
      }
    } catch (error) {
      console.error("Upload failed:", error);
      setModalState({
        open: true,
        type: "error",
        message: "File upload failed. Please try again.",
        redirectPath: "",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleBack = () => {
    navigate("/user/Admission-List");
  };

  // Upload
  const handleUpload = async () => {
    if (!formData.upload_path) {
      setModalState({
        open: true,
        type: "error",
        message: "Please upload a file first.",
        redirectPath: "",
      });
      return;
    }
    const payload = {
      academic_year: academic_year_id,
      school_id: schoolId,
      client_id: user?.client_data[0]?.client_id,
      upload_path: formData.upload_path,
      original_name: formData.original_name,
    };
    try {
      setIsProcessing(true);
      const response = await AxiosInstance.post("/upload-ocr-file", payload);
      if (response?.data?.status) {
        setModalState({
          open: true,
          type: "success",
          message: "Upload successful!",
          redirectPath: "",
        });
        fetchStaffList();
        setFormData({ file: null, original_name: null, upload_path: null });
      } else {
        setModalState({
          open: true,
          type: "error",
          message:
            response?.data?.message || "Upload failed. Please try again.",
          redirectPath: "",
        });
      }
    } catch (error) {
      console.error("Upload failed:", error);
      setModalState({
        open: true,
        type: "error",
        message: "Upload failed. Please try again.",
        redirectPath: "",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // Table List
  const fetchStaffList = useCallback(async () => {
    setLoading(true);
    try {
      const payload = {
        school_id: schoolId,
        created_at: moment(new Date()).format("YYYY-MM-DD"),
      };

      const response = await AxiosInstance.post("/ocrAdmissionList", payload);

      if (response?.data?.status) {
        setStaffList(response.data.data || []);
      } else {
        setStaffList([]);
      }
    } catch (error) {
      console.error("Error fetching list:", error);
      setStaffList([]);
    } finally {
      setLoading(false);
    }
  }, [schoolId]);

  useEffect(() => {
    if (schoolId) {
      fetchStaffList();
    }
  }, [schoolId, fetchStaffList]);

  return (
    <>
      <div className={`${styles.container} mt-4`}>
        <div className={styles.header}>
          <div className={styles.headerIcon}>
            <FaTable />
          </div>
          <h1 className={styles.headerTitle}>{translations.labels?.upload}</h1>
          <div className={styles.uploadActions}>
            <CustomButton
              type="button"
              variant="contained"
              className={styles.headerButton}
              fullWidth={false}
              onClick={handleBack}
            >
              {translations.labels.backAdmissionApplicationList}
            </CustomButton>
          </div>
        </div>
        {/* Table */}
        <CustomMRTTable
          columns={columns}
          data={staffList}
          loading={loading}
          searchInput={searchInput}
          placeholder={translations.labels?.searchName || "Search by name..."}
          setSearchInput={setSearchInput}
          onSearchEnter={(value) => {
            setApiSearch(value);
          }}
          enableSearch={false}
          enableFiltering={false}
          enablePagination={false}
        />
      </div>

      <div className={styles.container}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerIcon}>
            <FaTable />
          </div>
          <h1 className={styles.headerTitle}>
            {translations.labels?.backAdmissionApplicationList}
          </h1>
        </div>

        {/* Upload Form */}
        <div className={styles.uploadForm}>
          <div className="row">
            <div className={styles.uploadRow}>
              <CustomFileInput
                label={translations.labels.uploadYourFile}
                name="file"
                value={formData.original_name || null}
                onChange={handleFileChange}
                accept=".pdf,.jpg,.jpeg,.png"
                placeholder={
                  formData.original_name
                    ? formData.original_name
                    : "Choose file"
                }
                buttonText="Choose File"
                disabled={isUploading || isProcessing}
              />

              <CustomButton
                type="button"
                variant="contained"
                className={styles.uploadButton}
                onClick={handleUpload}
                disabled={isUploading || isProcessing}
              >
                {isUploading
                  ? "Uploading..."
                  : isProcessing
                    ? "Processing..."
                    : translations.labels.upload}
              </CustomButton>
            </div>
          </div>
        </div>
      </div>

      {/* PopupModal */}
      <PopupModal
        open={modalState.open}
        type={modalState.type}
        title={
          modalState.type === "success"
            ? "Success"
            : modalState.type || "Failed"
        }
        message={modalState.message}
        onOk={handleSuccessOk}
        onClose={handleModalClose}
      />
    </>
  );
}

export default OfflineApplicationUpload;
